/** @jsx isolateComponent */

import { Fragment, useEffect, useState } from 'react';

import {
  AsyncOverlay,
  Document,
  Link,
  MessageBar,
  TextInput,
} from '@zapier/common-components';

import { useLink } from '@zapier/common-hooks';
import { isolateComponent } from '@zapier/common-utils';
import { Margin, SkeletonBlock, Spacer, Text } from '@zapier/design-system';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import {
  ChangePlanCompleteModal,
  ConfirmSection,
  DowngradeForm,
} from '@zapier/design-system-revenue/src/components/other';

import { Interval, PlanV2 } from '@zapier/design-system-revenue/src/types';
import { downgradeSurveyQuestions } from '@zapier/design-system-revenue/src/utils';

import {
  useChangePlan,
  useCurrentAccountId,
  usePlans,
} from '@zapier/design-system-revenue/src/hooks';

import { DowngradeQuestion } from './DowngradeQuestion';

import {
  formatDowngradeSurveyFeedback,
  formatDowngradeSurveyFeedbackAsString,
} from './utils';

const DOWNGRADE_TO_FREE_COPY =
  'Your account will be credited for any unused time. You can use that credit anytime for future upgrades.';
const DOWNGRADE_TO_NON_FREE_COPY =
  'We’ll apply a credit for any unused time to your next scheduled payment.';

type DowngradeSurveyProps = {
  /** Modal to show after downgrading */
  Modal?: any;
  /** Function called when user cancels downgrade */
  onCancel: () => void;
  params: {
    interval: Interval;
    toPlanName: string;
    fromPlanName: string;
  };
};

type Items = {
  [key: number]: boolean;
};

export const DowngradeSurvey = ({
  params: { fromPlanName, interval, toPlanName },
}: DowngradeSurveyProps) => {
  const { routeTo } = useLink();
  const { trackEvent } = useTrackingContext();
  const { accountId } = useCurrentAccountId();
  const {
    data: { plans },
    isLoading,
  } = usePlans(accountId, { flatPlans: true });

  const toPlan = (plans as PlanV2[]).find(
    p => toPlanName === p.name.toLowerCase() && interval === p.interval
  );

  const [comments, setComments] = useState('');
  const [shuffledQuestions, setShuffledQuestions] = useState<string[]>([]);
  const [checkedItems, setCheckedItems] = useState<Items>(
    shuffledQuestions.reduce((obj, _q, i) => ({ ...obj, [i]: false }), {})
  );

  // Shuffle questions once on component mount
  useEffect(() => {
    setShuffledQuestions(downgradeSurveyQuestions());
  }, []);

  const questionWidgets = shuffledQuestions.map((question, i) => (
    <DowngradeQuestion
      isChecked={checkedItems[i] ?? false}
      key={i}
      onToggle={() =>
        setCheckedItems({ ...checkedItems, [i]: !checkedItems[i] })
      }
      question={question}
    />
  ));

  const userHasNotCheckedABox = !Object.values(checkedItems).some(
    value => value === true
  );

  const options = shuffledQuestions.map((question, i) => ({
    isChecked: checkedItems[i],
    question,
  }));

  const {
    changePlan,
    changePlanError,
    hasChangedPlan,
    isChangingPlan,
    pausedZaps,
  } = useChangePlan({
    accountId,
    planId: toPlan?.id,
    downgradeReason: formatDowngradeSurveyFeedbackAsString(options, comments),
  });

  const onDowngrade = async () => {
    trackEvent('Billing: downgrade survey', {
      accountId,
      ...formatDowngradeSurveyFeedback(options, comments),
    });
    return changePlan();
  };

  const onCancel = () => {
    trackEvent('Billing: cancel downgrade', {
      accountId,
      fromPlanName,
      plan: toPlanName,
      interval,
      step: 'downgrade survey',
    });
    if (routeTo) {
      return routeTo('/app/zaps');
    }
    return null;
  };

  const content = (
    <section>
      <Text color="grey900" tag="h3" type="subtitle700">
        Why are you downgrading?{' '}
        <Text tag="span" type="bodyText800">
          Your feedback is important to us.
        </Text>
      </Text>

      <Spacer height={20} />

      {questionWidgets}

      <Margin margin={'30px 0'}>
        <span id="downgrade-comments">
          <Text color="grey900" tag="h3" type="subtitle700">
            Is there anything else we should know?{' '}
            <Text fontWeight={400}>(optional)</Text>
          </Text>
        </span>

        <Spacer height={10} />

        <TextInput
          ariaLabelledBy="downgrade-comments"
          isMultiLine={true}
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            setComments(e.currentTarget.value)
          }
          placeholder="Add any additional comments."
          rows={5}
        />
      </Margin>

      <MessageBar margin="bottom" title="Heads up." type="warning">
        Your plan will be changed immediately.{' '}
        {toPlan?.amount === 0
          ? DOWNGRADE_TO_FREE_COPY
          : DOWNGRADE_TO_NON_FREE_COPY}
      </MessageBar>

      {changePlanError && (
        <MessageBar margin="bottom" title="Bargle!" type="error">
          {changePlanError}{' '}
          {changePlanError.includes('card') && (
            <Link href="/app/settings/billing" underline="always">
              Please update your payment details.
            </Link>
          )}
        </MessageBar>
      )}

      <ConfirmSection
        cancelText="Keep my current plan"
        confirmText="Downgrade now"
        isConfirmDisabled={isChangingPlan || userHasNotCheckedABox}
        onCancel={onCancel}
        onConfirm={onDowngrade}
      />
    </section>
  );

  return (
    <Document title="Downgrade Survey">
      <DowngradeForm header="One more thing" planName={toPlanName}>
        {isLoading ? (
          <SkeletonBlock height="100%" width="100%" />
        ) : (
          <Fragment>
            {hasChangedPlan && (
              <ChangePlanCompleteModal
                changeType={'downgrade'}
                hasPausedZaps={pausedZaps.length > 0}
                newPlan={toPlan as PlanV2}
              />
            )}
            <AsyncOverlay isVisible={isChangingPlan}>{content}</AsyncOverlay>
          </Fragment>
        )}
      </DowngradeForm>
    </Document>
  );
};
